"use client";

import Image from 'next/image';
import { useTranslations } from '@/lib/i18n-utils';
import Navbar from '@/components/navbar';
import LandingFooter from '@/components/LandingFooter';
import { getFeatureCategories } from '@/lib/navigation';
import { SignUpButton } from '@clerk/nextjs';
import {
  Search,
  MapPin,
  FileDown,
  UserPlus,
  Phone,
  Mail,
  Building2,
  ChevronRight,
  Briefcase,
  CheckCircle,
  ArrowRight,
} from 'lucide-react';

export default function CustomerDatabaseMarketingPage() {
  const { locale } = useTranslations();
  const featureCategories = getFeatureCategories(locale);

  const da = locale === 'da';

  const highlights = [
    {
      icon: Search,
      title: da ? 'Søg og filtrér hurtigt' : 'Instant search & filter',
      desc: da
        ? 'Find enhver kunde på sekunder med kraftfuld søgning og smarte filtre.'
        : 'Locate any customer in seconds with powerful search and smart filters.',
    },
    {
      icon: MapPin,
      title: da ? 'Lokationer & sager' : 'Sites & jobs',
      desc: da
        ? 'Se alle tilknyttede lokationer og aktive sager direkte på kundekortet.'
        : 'See all linked sites and active jobs right on the customer card.',
    },
    {
      icon: FileDown,
      title: da ? 'Eksportér til regneark' : 'Export to spreadsheet',
      desc: da
        ? 'Download hele kundedatabasen som CSV eller Excel med ét klik.'
        : 'Download your entire customer list as CSV or Excel in one click.',
    },
    {
      icon: UserPlus,
      title: da ? 'Tilføj & redigér' : 'Add & edit contacts',
      desc: da
        ? 'Opret nye kunder og opdatér kontaktoplysninger på farten — fra web eller mobil.'
        : 'Create new customers and update contact details on the go — from web or mobile.',
    },
  ];

  const contactFields = [
    { icon: Building2, label: da ? 'Firmanavn' : 'Company name', value: 'Mikkel Sørensen' },
    { icon: MapPin,    label: da ? 'Adresse' : 'Address',         value: 'Storekongensgade 12' },
    { icon: Mail,      label: da ? 'E-mail' : 'Email',             value: 'mikkel@examplemail.com' },
    { icon: Phone,     label: da ? 'Telefon' : 'Phone',            value: '12 34 56 78' },
  ];

  return (
    <>
      {/* ── Navbar ── */}
      <section className="relative overflow-hidden">
        <Navbar featureCategories={featureCategories} />
        <div aria-hidden="true" className="h-16 md:h-20" />
      </section>

      <main className="overflow-hidden bg-gradient-to-b from-white via-green-50/40 to-slate-50">

        {/* ════════════════════════════════════════
            HERO
        ════════════════════════════════════════ */}
        <section className="relative pt-16 pb-0 md:pt-24">
          {/* warm tinted blob behind content */}
          <div
            aria-hidden
            className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 h-[600px] w-[900px] rounded-full bg-gradient-radial from-[#d4ead9]/60 via-[#eef7ef]/30 to-transparent blur-3xl"
          />

          <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            {/* badge */}
            <div className="mb-6 flex justify-center md:justify-start">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#1a4731]/20 bg-white/80 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#1a4731] shadow-sm backdrop-blur-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-[#1a4731]" />
                {da ? 'Kundeadministration' : 'Customer Management'}
              </span>
            </div>

            <div className="flex flex-col md:flex-row md:items-end md:gap-16">
              {/* left: text */}
              <div className="md:w-[52%]">
                <h1 className="font-['Georgia',_serif] text-4xl font-bold leading-[1.1] tracking-tight text-[#1a1a1a] sm:text-5xl lg:text-[3.75rem]">
                  {da ? (
                    <>Alle dine kunder.<br /><span className="text-[#1a4731]">Samlet ét sted.</span></>
                  ) : (
                    <>All your customers.<br /><span className="text-[#1a4731]">One place.</span></>
                  )}
                </h1>

                <p className="mt-5 max-w-lg text-lg leading-relaxed text-slate-600">
                  {da
                    ? "Svendetid's kundekartotek giver dig et komplet overblik over kontakter, lokationer og historik — tilgængeligt fra web og mobil."
                    : "Svendetid's customer database gives you a complete view of contacts, sites, and history — accessible from web and mobile."}
                </p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <SignUpButton mode="modal" fallbackRedirectUrl="/org/create">
                    <button
                      type="button"
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#1a4731] px-7 py-3.5 text-base font-semibold text-white shadow-lg transition hover:bg-[#153a28] hover:shadow-xl active:scale-[0.98]"
                    >
                      {da ? 'Prøv gratis' : 'Try for free'}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </SignUpButton>
                  
                </div>
              </div>

              {/* right: web screenshot */}
              <div className="mt-12 md:mt-0 md:w-[48%] md:self-end">
                <div className="relative overflow-hidden rounded-t-2xl border border-b-0 border-slate-200/80 shadow-[0_-8px_40px_rgba(0,0,0,0.10)]">
                
                  <Image
                    src="/web-customer.png"
                    alt={da ? 'Svendetid kundeoversigt – webvisning' : 'Svendetid customer view – web'}
                    width={700}
                    height={500}
                    className="w-full object-cover object-top"
                    priority
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════
            FEATURE HIGHLIGHTS  (4 cards)
        ════════════════════════════════════════ */}
        <section className="py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {highlights.map(({ icon: Icon, title, desc }) => (
                <div
                  key={title}
                  className="group rounded-2xl border border-slate-200/70 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-md"
                >
                  <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#eef7ef] text-[#1a4731] transition group-hover:bg-[#1a4731] group-hover:text-white">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mb-1.5 font-['Georgia',_serif] text-base font-bold text-slate-900">{title}</h3>
                  <p className="text-sm leading-relaxed text-slate-500">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════
            SPLIT SECTION: contact card mockup + mobile screenshot
        ════════════════════════════════════════ */}
        <section className="py-16 md:py-24">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col gap-12 md:flex-row md:items-center md:gap-16">

              {/* left: text + interactive contact card mockup */}
              <div className="md:w-1/2">
                <h2 className="font-['Georgia',_serif] text-3xl font-bold leading-tight text-slate-900 sm:text-4xl">
                  {da
                    ? 'Alt om kunden — samlet på ét kort'
                    : 'Everything about a customer — on one card'}
                </h2>
                <p className="mt-4 text-base leading-relaxed text-slate-600">
                  {da
                    ? 'Fra kontaktoplysninger og adresse til aktive jobs og fakturaer. Svendetid holder styr på det hele, så du kan fokusere på arbejdet.'
                    : 'From contact details and address to active jobs and invoices. Svendetid tracks it all so you can focus on the work.'}
                </p>

                {/* Animated contact card */}
                <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg">
                  {/* card header */}
                  <div className="flex items-center gap-3 border-b border-slate-100 bg-[#f7f6f2] px-5 py-4">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1a4731] text-sm font-bold text-white">
                      MS
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">Mikkel Sørensen</p>
                      <p className="text-xs text-slate-500">{da ? 'Kunde siden 2023' : 'Customer since 2023'}</p>
                    </div>
                    <div className="ml-auto">
                      <span className="rounded-full bg-[#eef7ef] px-2.5 py-0.5 text-xs font-medium text-[#1a4731]">
                        3 jobs
                      </span>
                    </div>
                  </div>

                  {/* tabs */}
                  <div className="flex overflow-x-auto border-b border-slate-100 text-xs font-semibold">
                    {(da
                      ? ['Kundeoplysninger', 'Tilbud', 'Jobs', 'Fakturaer', 'Lokationer']
                      : ['Customer info', 'Quotes', 'Jobs', 'Invoices', 'Sites']
                    ).map((tab, i) => (
                      <button
                        key={tab}
                        type="button"
                        className={`flex-shrink-0 px-4 py-2.5 transition ${
                          i === 0
                            ? 'border-b-2 border-[#1a4731] text-[#1a4731]'
                            : 'text-slate-400 hover:text-slate-600'
                        }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>

                  {/* fields */}
                  <div className="divide-y divide-slate-50 px-5 py-1">
                    {contactFields.map(({ icon: Icon, label, value }) => (
                      <div key={label} className="flex items-center gap-3 py-3">
                        <Icon className="h-4 w-4 flex-shrink-0 text-slate-400" />
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
                          <p className="truncate text-sm font-medium text-slate-800">{value}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* active jobs */}
                  <div className="border-t border-slate-100 px-5 pb-4 pt-3">
                    <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                      {da ? 'Aktive opgaver (1)' : 'Active jobs (1)'}
                    </p>
                    <div className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 text-sm">
                      <Briefcase className="h-4 w-4 flex-shrink-0 text-[#1a4731]" />
                      <span className="flex-1 text-slate-700">
                        {da
                          ? 'Reparation af terrasse og udskiftning af rådskadet bræt'
                          : 'Terrace repair and replacement of rotten plank'}
                      </span>
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </div>
                  </div>
                </div>
              </div>

              {/* right: mobile screenshot in phone frame */}
              <div className="flex justify-center md:w-1/2 md:justify-end">
                <div className="relative">
                  {/* ambient glow */}
                  <div
                    aria-hidden
                    className="absolute inset-0 scale-90 rounded-[3rem] bg-[#1a4731]/10 blur-2xl"
                  />
                 
                    <Image
                      src="/mobile,customer,database.png"
                      alt={da ? 'Svendetid kundeoversigt – mobilvisning' : 'Svendetid customer view – mobile'}
                      width={280}
                      height={560}
                      className="block w-[260px] sm:w-[280px]"
                      priority
                    />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ════════════════════════════════════════
            DARK CTA + BENEFITS CHECKLIST
        ════════════════════════════════════════ */}
        <section className="py-16 md:py-20">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="overflow-hidden rounded-3xl bg-[#1a4731]">
              <div className="flex flex-col gap-0 md:flex-row">
                {/* left: headline + CTA */}
                <div className="flex flex-col justify-center px-8 py-12 md:w-2/5 md:px-12 md:py-16">
                  <h2 className="font-['Georgia',_serif] text-3xl font-bold leading-tight text-white sm:text-4xl">
                    {da ? 'Bygget til håndværkere i felten' : 'Built for tradespeople in the field'}
                  </h2>
                  <p className="mt-4 text-base leading-relaxed text-white/70">
                    {da
                      ? 'Hurtig adgang til kundeoplysninger, uanset om du er på kontoret eller ude på en opgave.'
                      : "Fast access to customer details whether you're in the office or on a job site."}
                  </p>
                  <SignUpButton mode="modal" fallbackRedirectUrl="/org/create">
                    <button
                      type="button"
                      className="mt-8 inline-flex w-fit items-center gap-2 rounded-xl bg-white px-6 py-3 text-sm font-semibold text-[#1a4731] shadow transition hover:bg-white/90 active:scale-[0.98]"
                    >
                      {da ? 'Kom i gang gratis' : 'Get started free'}
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </SignUpButton>
                </div>

                {/* right: checklist */}
                <div className="border-t border-white/10 px-8 py-12 md:w-3/5 md:border-l md:border-t-0 md:px-12 md:py-16">
                  <ul className="space-y-5">
                    {(da
                      ? [
                          'Søg og find kunder lynhurtigt med navn, adresse eller telefonnummer',
                          'Se alle tilknyttede sager, tilbud og fakturaer direkte på kundekortet',
                          'Tilføj og redigér kontaktoplysninger fra web eller mobil-app',
                          'Eksportér din kundedatabase til CSV eller Excel når som helst',
                          'Tilknyt flere lokationer og kontaktpersoner til samme kunde',
                          'Fuld historik: hvem ringede, hvornår og hvad blev aftalt',
                        ]
                      : [
                          'Search and find customers instantly by name, address, or phone',
                          'See all linked jobs, quotes, and invoices directly on the customer card',
                          'Add and edit contact details from web or mobile app',
                          'Export your customer database to CSV or Excel at any time',
                          'Link multiple sites and contacts to the same customer',
                          'Full history: who called, when, and what was agreed',
                        ]
                    ).map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#6fcf97]" />
                        <span className="text-sm leading-relaxed text-white/85">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

      </main>
      <LandingFooter lang={locale === "da" ? "da" : "en"} daHref="/features/customer-database" enHref="/en/features/customer-database" />
    </>
  );
}
